<script setup>
import { ref } from "vue";
import { useRouter } from "vue-router";
import AppDataTable from "@/components/AppDataTable.vue";
import ConfirmDialog from "@/components/ConfirmDialog.vue";
import { useResourceList } from "@/composables/useResourceList";
import { fetchCampaignVideos, deleteCampaignVideo } from "./api";

const router = useRouter();
const columns = [
  { key: "title",    label: "Title",    sortable: true },
  { key: "sequence", label: "Seq." },
  { key: "duration", label: "Duration" },
  { key: "enabled",  label: "Enabled" },
  { key: "actions",  label: "Actions",  align: "end", width: "230px" },
];
const rawItems = ref([]);
const { rows, loading, error, pendingDelete, deleting, load, askDelete, confirmDelete } =
  useResourceList({
    fetch: () => fetchCampaignVideos().then((data) => { rawItems.value = Array.isArray(data) ? data : []; return data; }),
    remove: deleteCampaignVideo,
    mapRow: (v) => ({ id: v._id, title: v.title, sequence: v.sequence ?? v.order ?? "—", duration: v.duration ?? "—", enabled: v.enable ? "Yes" : "No" }),
  });

const previewDialog = ref(false);
const previewItem = ref(null);
function openPreview(row) {
  previewItem.value = rawItems.value.find((v) => v._id === row.id) ?? null;
  previewDialog.value = true;
}
</script>

<template>
  <section>
    <div class="d-flex align-center justify-space-between mb-4 gap-3 flex-wrap">
      <h1 class="text-h5">Campaign Videos</h1>
      <div class="d-flex gap-2">
        <VBtn variant="tonal" @click="load">Refresh</VBtn>
        <VBtn @click="router.push({ name: 'campaign-videos-new' })">Add Video</VBtn>
      </div>
    </div>
    <VAlert v-if="error" type="error" closable class="mb-4" @click:close="error = ''">{{ error }}</VAlert>
    <AppDataTable :columns="columns" :rows="rows" :loading="loading" row-key="id" searchable search-placeholder="Search…" empty-text="No campaign videos yet.">
      <template #cell-actions="{ row }">
        <VBtn size="small" variant="text" @click.stop="openPreview(row)">Preview</VBtn>
        <VBtn size="small" variant="text" @click.stop="router.push({ name: 'campaign-videos-edit', params: { id: row.id } })">Edit</VBtn>
        <VBtn size="small" color="error" variant="text" @click.stop="askDelete(row)">Delete</VBtn>
      </template>
    </AppDataTable>
    <ConfirmDialog :model-value="pendingDelete !== null" title="Delete campaign video?" :message="`Delete &quot;${pendingDelete?.title || 'this video'}&quot;?`" :loading="deleting" @update:model-value="pendingDelete = null" @confirm="confirmDelete" />

    <VDialog v-model="previewDialog" max-width="480" scrollable>
      <VCard>
        <VCardTitle class="pt-4 px-4">{{ previewItem?.title ?? 'Video Preview' }}</VCardTitle>
        <VCardText class="pa-3">
          <div v-if="!previewItem" class="text-disabled pa-4 text-center">No data available.</div>
          <div v-else>
            <video v-if="previewItem.videoUrl" :src="previewItem.videoUrl" controls style="width:100%;border-radius:6px" />
            <VImg v-else-if="previewItem.thumbnailUrl" :src="previewItem.thumbnailUrl" class="rounded" />
            <div v-else class="text-disabled text-center">No preview available.</div>
            <div class="text-caption text-disabled mt-2">Duration: {{ previewItem.duration ?? '—' }} &bull; Seq: {{ previewItem.sequence ?? previewItem.order ?? '—' }}</div>
          </div>
        </VCardText>
        <VCardActions class="px-4 pb-4 justify-end">
          <VBtn @click="previewDialog = false">Close</VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
  </section>
</template>
